import * as THREE from 'three';
import { InteractiveStar } from './InteractiveStar.js';
import { createMarkerStar } from '../utils/createMarkerStar.js';

const EUROPA_CARD = `
  <h2>Europa</h2>
  <div class="label">Jupiter's icy moon</div>

  <p>This is Europa. It is a moon orbiting Jupiter. It is one of the most interesting objects in the solar system, and astronomers will not stop arguing about it.</p>

  <p>The reason is what is underneath the ice.</p>

  <p>Europa's surface is frozen solid. But below the ice - kilometers down - there is an ocean. A real ocean. Bigger than all the oceans on Earth combined. It has been there for billions of years. It is still there now. Nobody on Earth has ever touched it.</p>

  <p>Some scientists think it might have life in it. Some scientists think it doesn't. They will eventually send a probe to find out.</p>

  <hr>

  <p>You don't need a probe. You already know what is down there.</p>

  <p>Some people are drawn to mountains. Some to sky. You have always been drawn to water. Pools, oceans, lakes - it doesn't matter. You just need to be in it.</p>

  <p>You and Europa have that in common. Somewhere underneath all that surface, there is something alive that just wants to swim.</p>
`;

/**
 * Create Europa + Jupiter.
 *
 * Europa is placed at the **real position of Jupiter** as it appeared from
 * Bangalore at midnight April 2, 1998 IST (computed via astronomy-engine).
 * Jupiter is rendered as a small non-interactive warm-white sprite right
 * next to Europa, so the visitor sees a pair of dots - the brighter is
 * Jupiter, the smaller icy one is Europa.
 *
 * This honors astronomical reality: Europa is genuinely a moon of Jupiter,
 * and to see it from Earth you have to look at Jupiter.
 *
 * Returns { europa, jupiter } - caller adds both to the sky.
 */
export function createEuropaAndJupiter({ starField, onClick }) {
  const jupiterPos = starField.getPlanetPosition('Jupiter');
  if (!jupiterPos) {
    console.warn('Could not compute Jupiter position; falling back to manual placement');
    return { europa: null, jupiter: null };
  }

  // Europa offset from Jupiter - exaggerated from real ~1 arcminute so the
  // two dots are visually distinct and individually clickable
  const offset = new THREE.Vector3(14, 8, 0);
  const europaPos = jupiterPos.clone().add(offset);

  // Jupiter - non-interactive marker rendered with the SAME shader as the
  // background star catalog, so it looks like a regular bright cataloged
  // star, not a "click me" interactive glow sprite. Slightly larger than
  // most cataloged stars (Jupiter at peak brightness is mag -2.94 in real
  // life, similar to Sirius).
  const jupiter = createMarkerStar(jupiterPos, 0xfff5d8, 12);

  // Europa - interactive icy dot, slightly smaller than Jupiter
  const europa = new InteractiveStar({
    position: europaPos,
    color: 0xc8e8ff,
    glowColor: '200, 232, 255',
    scale: 14,
    pulseSpeed: 0.6,
    pulseAmount: 0.04,
    discoveryId: 'europa',
    category: 'hobbies',
    cardHTML: EUROPA_CARD,
    onClick,
  });

  return { europa, jupiter };
}
